import React, { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

export const RouteLeavingGuardComponent = ({ children, shouldBlockNavigation, when }) => {
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const showModal = useCallback(
    (location) => {
      setVisible(true);
      setLastLocation(location);
    },
    [setVisible, setLastLocation]
  );

  const closeModal = useCallback(
    (callback) => {
      setVisible(false);
      return typeof callback === 'function' && callback();
    },
    [setVisible]
  );

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
        showModal(nextLocation);
        return false;
      }

      return true;
    },
    [confirmedNavigation, shouldBlockNavigation, showModal]
  );

  const handleConfirmNavigationClick = useCallback(() => {
    closeModal(() => {
      if (!lastLocation) {
        return;
      }

      setConfirmedNavigation(true);
    });
  }, [closeModal, lastLocation, setConfirmedNavigation]);

  useEffect(() => {
    if (confirmedNavigation) {
      setConfirmedNavigation(false);
      const { pathname, state: { activeTab, preselect, workflowStartPath } = {} } =
        lastLocation || {};
      // Navigate to the previous blocked location
      history.push({
        pathname,
        state: {
          workflowStartPath,
          preselect,
          activeTab
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedNavigation]);

  const props = {
    onCancel: closeModal,
    onConfirm: handleConfirmNavigationClick
  };

  return (
    <>
      <Prompt
        when={visible || shouldBlockNavigation(lastLocation) || when}
        message={handleBlockedNavigation}
      />
      {visible &&
        children({
          ...props
        })}
    </>
  );
};

RouteLeavingGuardComponent.propTypes = {
  when: PropTypes.bool,
  children: PropTypes.func,
  shouldBlockNavigation: PropTypes.func
};
RouteLeavingGuardComponent.defaultProps = {
  when: false,
  children() {},
  shouldBlockNavigation() {}
};

export default RouteLeavingGuardComponent;
